import { createClient } from '@supabase/supabase-js'
import { collection, doc, getDoc, getDocs } from 'firebase/firestore'

import { db as firebaseDb } from '@/firebase'
import { supabase } from '@/supabase'
import { createRealtimeSessionId } from '@/utils/supabase-realtime'

const supabaseUrl = process.env.VUE_APP_SUPABASE_URL
const supabaseAnonKey = process.env.VUE_APP_SUPABASE_ANON_KEY
const OWNER_HASH_HEADER = 'x-live-chat-hash'
const MESSAGE_MAX_LEN = 500
const LINK_REGEX = /(?:http|https|ftp|ftps):\/\/[^\s/$.?#].[^\s]*/g

const getProjectId = ({project, projectId} = {}) => {
  const id = projectId ?? project?.id

  if (id === undefined || id === null || id === '') {
    throw new Error('Live chat project id is not available')
  }

  return String(id)
}

const ensureSupabase = (client = supabase) => {
  if (!client) {
    throw new Error('Supabase client is not available')
  }

  return client
}

const createOwnerSupabaseClient = (hash) => {
  if (!hash) {
    throw new Error('Live chat owner hash is required')
  }

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase config is missing')
  }

  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false
    },
    global: {
      headers: {
        [OWNER_HASH_HEADER]: hash
      }
    }
  })
}

const throwIfSupabaseError = (error, fallbackMessage) => {
  if (!error) {
    return
  }

  const wrapped = new Error(error.message || fallbackMessage || 'Live chat request failed')
  wrapped.data = error
  throw wrapped
}

const sanitizeMessageText = (text = '') => {
  return String(text)
    .slice(0, MESSAGE_MAX_LEN)
    .replace(LINK_REGEX, '[Link Removed]')
}

const toProfileBadges = (memberData = {}) => {
  return Array.isArray(memberData.crmTags)
    ? memberData.crmTags.map(tag => tag.tag_id)
    : []
}

export const getLiveChatPublicData = async ({project, projectId} = {}) => {
  const resolvedProjectId = getProjectId({project, projectId})
  const groupRef = doc(firebaseDb, 'social', resolvedProjectId)
  const accessGroupsRef = collection(firebaseDb, 'social', resolvedProjectId, 'access-groups')

  const [groupSnapshot, badgesSnapshot] = await Promise.all([
    getDoc(groupRef),
    getDocs(accessGroupsRef)
  ])

  return {
    badges: badgesSnapshot.docs.map(badge => ({
      id: badge.id,
      ...badge.data()
    })),
    group: groupSnapshot.exists() ? groupSnapshot.data() : {}
  }
}

export const getLiveChatUserExp = async ({project, userId, projectId} = {}) => {
  const resolvedProjectId = getProjectId({project, projectId})
  const userRef = doc(firebaseDb, 'social', resolvedProjectId, 'exp', String(userId))
  const snapshot = await getDoc(userRef)

  if (!snapshot.exists()) {
    return {
      id: String(userId),
      points: 0
    }
  }

  return {
    id: snapshot.id,
    ...snapshot.data()
  }
}

export const getLiveChatProfile = async ({project, profileId, projectId} = {}) => {
  const resolvedProjectId = getProjectId({project, projectId})
  const resolvedProfileId = String(profileId)
  const memberRef = doc(firebaseDb, 'social', resolvedProjectId, 'members', resolvedProfileId)
  const expRef = doc(firebaseDb, 'social', resolvedProjectId, 'exp', resolvedProfileId)

  const [memberSnapshot, expSnapshot] = await Promise.all([
    getDoc(memberRef),
    getDoc(expRef)
  ])

  const memberData = memberSnapshot.exists() ? memberSnapshot.data() : {}
  const expData = expSnapshot.exists() ? expSnapshot.data() : {}

  return {
    id: resolvedProfileId,
    email: memberData.author?.email,
    name: memberData.author?.displayName,
    avatar: memberData.author?.avatar,
    badges: toProfileBadges(memberData),
    exp: expData?.points || 0,
    ...memberData
  }
}

export const ensureLiveChatRoomExists = async ({roomId, hash, userId} = {}) => {
  const client = ensureSupabase()

  const { data, error } = await client
    .from('public_chat_rooms')
    .select('old_id')
    .eq('old_id', roomId)
    .maybeSingle()

  if (error) {
    console.warn('[liveChat] Failed to check room existence', error)
  }

  if (data) {
    return { id: roomId, created: false }
  }

  const ownerClient = createOwnerSupabaseClient(hash)

  const { error: roomError } = await ownerClient.from('public_chat_rooms').insert({
    old_id: roomId,
    user_id: userId ? Number(userId) : null,
    live: '0',
    message: '',
    activated: false
  })

  throwIfSupabaseError(roomError, 'Unable to create live chat room from Firebase data')

  const { error: accessError } = await ownerClient.from('public_chat_room_access').insert({
    room_id: roomId,
    owner_hash: hash
  })

  throwIfSupabaseError(accessError, 'Unable to create live chat room access')

  return { id: roomId, created: true }
}

export const createLiveChatRoom = async ({hash, userId} = {}) => {
  const ownerClient = createOwnerSupabaseClient(hash)
  const roomId = createRealtimeSessionId('room')

  const { error: roomError } = await ownerClient.from('public_chat_rooms').insert({
    old_id: roomId,
    user_id: userId ? Number(userId) : null,
    live: '0',
    message: '',
    activated: false
  })

  throwIfSupabaseError(roomError, 'Unable to create live chat room')

  const { error: accessError } = await ownerClient.from('public_chat_room_access').insert({
    room_id: roomId,
    owner_hash: hash
  })

  throwIfSupabaseError(accessError, 'Unable to create live chat room access')

  return {
    id: roomId
  }
}

const getPublicSiteBase = (project = {}) => {
  const subdomain = project?.subdomain
  const domain = project?.domain

  if (domain) {
    const clean = domain.replace(/^https?:\/\//i, '')
    return `https://${clean}`
  }

  if (!subdomain) {
    return null
  }

  const siteHost = process.env.VUE_APP_ESTAGE_SITE_HOST || ''

  if (process.env.NODE_ENV === 'development') {
    return `http://${subdomain}.estage.localhost:3000`
  }

  const host = siteHost.replace(/^https?:\/\//i, '')
  return `https://${subdomain}.${host}`
}

export const updateLiveChatRoomState = async ({roomId, hash, live, message, activated, project} = {}) => {
  const base = getPublicSiteBase(project)

  if (base) {
    const params = new URLSearchParams()
    params.set('hash', hash)

    if (live !== undefined) params.set('live', String(live))
    if (message !== undefined) params.set('message', message || '')
    if (activated !== undefined) params.set('activated', activated ? '1' : '0')

    const response = await fetch(`${base}/api/live-chat/update-state/${encodeURIComponent(roomId)}?${params.toString()}`)
    const result = await response.json()

    if (!response.ok) {
      throw new Error(result?.statusMessage || result?.message || 'Unable to update live chat room state')
    }

    return { success: true }
  }

  // Fallback: direct Supabase update (requires matching access hash)
  const payload = {}

  if (live !== undefined) {
    payload.live = String(live)
  }

  if (message !== undefined) {
    payload.message = message || ''
  }

  if (activated !== undefined) {
    payload.activated = Boolean(activated)
  }

  if (!Object.keys(payload).length) {
    return { success: true }
  }

  const ownerClient = createOwnerSupabaseClient(hash)
  const { error } = await ownerClient
    .from('public_chat_rooms')
    .update(payload)
    .eq('old_id', roomId)

  throwIfSupabaseError(error, 'Unable to update live chat room state')

  return { success: true }
}

export const sendLiveChatMessage = async ({roomId, payload} = {}) => {
  const client = ensureSupabase()
  const messageData = {
    room_id: roomId,
    text: sanitizeMessageText(payload?.text),
    username: payload?.username || 'User',
    email: payload?.email || null,
    avatar: payload?.avatar || null,
    client_id: payload?.clientId || null,
    badges: Array.isArray(payload?.badges) ? payload.badges : []
  }

  if (payload?.id) {
    messageData.user_id = Number(payload.id)
  }

  if (payload?.exp !== undefined && payload?.exp !== null) {
    messageData.exp = Number(payload.exp)
  }

  const { error } = await client.from('public_chat_messages').insert(messageData)

  throwIfSupabaseError(error, 'Unable to send live chat message')

  return {
    status: 200,
    message: 'Message sent successfully'
  }
}

export const deleteLiveChatMessage = async ({roomId, messageId, hash} = {}) => {
  const ownerClient = createOwnerSupabaseClient(hash)

  const { error: deleteError } = await ownerClient
    .from('public_chat_messages')
    .delete()
    .eq('room_id', roomId)
    .eq('id', messageId)

  throwIfSupabaseError(deleteError, 'Unable to delete live chat message')

  const { error: deletionError } = await ownerClient.from('public_chat_message_deletions').insert({
    room_id: roomId,
    message_id: messageId
  })

  throwIfSupabaseError(deletionError, 'Unable to publish live chat deletion')

  return {
    success: true
  }
}

export const removeAllLiveChatMessages = async ({roomId, email, hash} = {}) => {
  const client = ensureSupabase()
  const ownerClient = createOwnerSupabaseClient(hash)

  const { data: messages, error: messagesError } = await client
    .from('public_chat_messages')
    .select('id')
    .eq('room_id', roomId)
    .eq('email', email)

  throwIfSupabaseError(messagesError, 'Unable to load live chat messages')

  const messageIds = (messages || []).map(message => message.id)

  if (messageIds.length) {
    const { error: deleteError } = await ownerClient
      .from('public_chat_messages')
      .delete()
      .eq('room_id', roomId)
      .eq('email', email)

    throwIfSupabaseError(deleteError, 'Unable to delete live chat messages')

    const { error: deletionInsertError } = await ownerClient
      .from('public_chat_message_deletions')
      .insert(messageIds.map(messageId => ({
        room_id: roomId,
        message_id: messageId
      })))

    throwIfSupabaseError(deletionInsertError, 'Unable to publish live chat deletions')
  }

  return {
    status: 200
  }
}

export const banLiveChatMessage = async ({roomId, messageId, hash} = {}) => {
  const client = ensureSupabase()
  const ownerClient = createOwnerSupabaseClient(hash)

  const { data: message, error: messageError } = await client
    .from('public_chat_messages')
    .select('email')
    .eq('room_id', roomId)
    .eq('id', messageId)
    .maybeSingle()

  throwIfSupabaseError(messageError, 'Unable to load live chat message')

  if (!message?.email) {
    return {
      status: 404
    }
  }

  const { error } = await ownerClient.from('public_chat_room_bans').upsert(
    {
      room_id: roomId,
      email: message.email
    },
    {
      onConflict: 'room_id,email'
    }
  )

  throwIfSupabaseError(error, 'Unable to ban live chat user')

  return {
    status: 200
  }
}

export const createProjectLiveChatService = ({project} = {}) => ({
  ensureRoomExists: ({roomId, hash, userId} = {}) => ensureLiveChatRoomExists({roomId, hash, userId}),
  createRoom: ({hash, userId} = {}) => createLiveChatRoom({hash, userId}),
  updateRoomState: ({roomId, hash, live, message, activated} = {}) =>
    updateLiveChatRoomState({roomId, hash, live, message, activated, project}),
  getPublicData: ({projectId} = {}) => getLiveChatPublicData({project, projectId}),
  getUserExp: ({userId, projectId} = {}) => getLiveChatUserExp({project, userId, projectId}),
  getProfile: ({profileId, projectId} = {}) => getLiveChatProfile({project, profileId, projectId}),
  sendMessage: ({roomId, payload} = {}) => sendLiveChatMessage({roomId, payload}),
  deleteMessage: ({roomId, messageId, hash} = {}) => deleteLiveChatMessage({roomId, messageId, hash}),
  removeAllMessages: ({roomId, email, hash} = {}) => removeAllLiveChatMessages({roomId, email, hash}),
  banMessage: ({roomId, messageId, hash} = {}) => banLiveChatMessage({roomId, messageId, hash})
})

export default {
  createProjectLiveChatService,
  ensureLiveChatRoomExists,
  createLiveChatRoom,
  updateLiveChatRoomState,
  getLiveChatPublicData,
  getLiveChatUserExp,
  getLiveChatProfile,
  sendLiveChatMessage,
  deleteLiveChatMessage,
  removeAllLiveChatMessages,
  banLiveChatMessage
}
